// May this person create a group in this project? The server's answer, asked once per
// project.
//
// The app reads the same thing off its feature map: "New group" shows with Group Chats
// AND Create Groups (apps/mobile/src/screens/social/DmConversationsScreen.tsx). The web
// does not read that map: it names the person from the address, not from the sign-in,
// and cannot tell whether a super admin's sign-in was validated, which is what the
// message routes go by (services/social-message-tick.ts on the server). So the page asks
// a message route instead: the group picker (GET /social-dm/groups/directory) makes
// exactly the checks creating a group makes (in the project or a validated super admin,
// Group Chats, Create Groups), from the sign-in alone, and refuses with the same plain
// 404 otherwise.
//
// It is asked with one character (messagesAPI.groupCreateCheck), not with nothing typed:
// the checks are the same, but nothing typed also looks up the person's whole team
// (which can wait on 1Office), and the page would only throw that list away. One
// character is answered with an empty list straight after the checks.
//
// 'unsure' is an answer that did not come (a fault, or a sign-in the server could not
// name). "New group" is not offered on it, and the page asks again on its next refresh.
import { useCallback, useEffect, useRef, useState } from 'react';
import { messagesAPI } from '../../services/messagesApi';

type Answer = 'yes' | 'no' | 'unsure';

const statusOf = (err: unknown): number | undefined =>
  (err as { response?: { status?: number } } | null)?.response?.status;

const askServer = (projectId: number): Promise<Answer> =>
  Promise.resolve()
    .then(() => messagesAPI.groupCreateCheck(projectId))
    .then(
      () => 'yes' as const,
      (err) => {
        const status = statusOf(err);
        return status === 404 || status === 403 ? 'no' : 'unsure';
      },
    );

/**
 * `round` starts the question over (the page's Try again). Answers `may` for the
 * project selected now only, and `askAgainIfUnsure` for the page's refresh.
 */
export function useMayCreateGroup(projectId: number | null, round: number): { may: boolean; askAgainIfUnsure: () => void } {
  // Kept WITH the project it was asked for: an answer for the project left behind is never used.
  const [held, setHeld] = useState<{ projectId: number; answer: Answer } | null>(null);
  const heldNow = useRef(held);
  useEffect(() => { heldNow.current = held; }, [held]);
  const asking = useRef(false);

  useEffect(() => {
    if (projectId === null) return undefined;
    let current = true;
    asking.current = true;
    void askServer(projectId).then((answer) => {
      asking.current = false;
      if (current) setHeld({ projectId, answer });
    });
    return () => { current = false; };
  }, [projectId, round]);

  const askAgainIfUnsure = useCallback(() => {
    const was = heldNow.current;
    if (projectId === null || asking.current || !was || was.projectId !== projectId || was.answer !== 'unsure') return;
    asking.current = true;
    void askServer(projectId).then((answer) => {
      asking.current = false;
      setHeld((latest) => (latest && latest.projectId === projectId ? { projectId, answer } : latest));
    });
  }, [projectId]);

  return { may: held !== null && held.projectId === projectId && held.answer === 'yes', askAgainIfUnsure };
}
